import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Home-page gallery teaser. Surfaces 4 curated photos from the
 * `photo_gallery` collection in a fixed 4-tile bento layout — a tall
 * hero on the left, two square tiles top-right, and a wide tile spanning
 * the bottom-right. Each tile links to the full `/gallery` page.
 * No heading, subtitle, or CTA — the imagery speaks for itself.
 * Filters out non-property placeholder tiles (floorplans, logos, generic
 * "other" items) so only real property photos surface on Home.
 * Photos with description "FEATURED" are sorted to the front of the preview.
 */

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  alt_text?: string | null;
  description?: string | null;
}

const PREVIEW_LIMIT = 4;
const EXCLUDED_TITLES = new Set(['other', 'floorplan', 'logo']);

function isFeaturedPhoto(photo: GalleryPhoto): boolean {
  return (photo.description ?? '').toLowerCase().trim() === 'featured';
}

export function HomeGalleryPreview() {
  // Load the full gallery so FEATURED photos beyond the first few can surface
  // before we slice down to the 4-tile preview.
  const images = useCollectionBinding<GalleryPhoto>('homegallerypreview.photos', {
    collection: 'photo_gallery',
  });

  const previewPhotos = useMemo(() => {
    const list = images ?? [];
    return list
      .filter((img) => {
        const src = img.image ?? img.original_url ?? '';
        if (!src) return false;
        return !EXCLUDED_TITLES.has((img.title ?? '').toLowerCase().trim());
      })
      .sort((a, b) => Number(isFeaturedPhoto(b)) - Number(isFeaturedPhoto(a)))
      .slice(0, PREVIEW_LIMIT);
  }, [images]);

  useRevealStyles();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 90 });

  const hasPhotos = previewPhotos.length > 0;

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {hasPhotos ? (
          <div ref={gridRef} className="hgp-grid">
            {previewPhotos.map((photo, i) => {
              const src = photo.image ?? photo.original_url ?? '';
              const alt = photo.alt_text ?? photo.title ?? 'Property photo';
              return (
                <a
                  key={src || i}
                  data-reveal
                  href="/gallery"
                  className={`reveal-fade-up hgp-tile hgp-tile--${i + 1} group relative overflow-hidden rounded-lg block`}
                  aria-label={alt}
                >
                  <VibesitesImage
                    url={src}
                    alt={alt}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    sizes="(min-width: 1024px) 50vw, (min-width: 640px) 50vw, 100vw"
                    priority={i < 2}
                  />
                </a>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text)]/60">
            <p className="font-body text-lg">Gallery photos will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        /* Fixed 4-tile bento layout:
             ┌───────┬───┬───┐
             │       │ 2 │ 3 │
             │   1   ├───┴───┤
             │       │   4   │
             └───────┴───────┘
           Tile 1 (tall hero) anchors the left, tiles 2/3 sit top-right
           as squares, tile 4 spans the full bottom-right as a wide
           landscape. Uses explicit grid-column/grid-row placement (not
           auto-flow) so the layout stays predictable regardless of tile
           order in the DOM. */
        .hgp-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
          gap: 0.5rem;
          height: 90vw;
          max-height: 620px;
        }

        .hgp-tile {
          grid-column: span 1;
          grid-row: span 1;
          min-width: 0;
          min-height: 0;
        }

        /* Mobile fallback (< 768px): stack tile 1 as a full-width hero,
           then a 2-col row for tiles 2 & 3, then tile 4 full-width. */
        .hgp-tile--1 {
          grid-column: 1 / span 2;
          grid-row: 1;
        }
        .hgp-tile--2 {
          grid-column: 1;
          grid-row: 2;
        }
        .hgp-tile--3 {
          grid-column: 2;
          grid-row: 2;
        }
        .hgp-tile--4 {
          grid-column: 1 / span 2;
          grid-row: 3;
        }

        @media (max-width: 767px) {
          .hgp-grid {
            grid-template-rows: 42vw 30vw 34vw;
            height: auto;
            max-height: none;
          }
        }

        /* md+ : 4-column × 2-row bento matching the reference layout. */
        @media (min-width: 768px) {
          .hgp-grid {
            grid-template-columns: repeat(4, 1fr);
            grid-template-rows: repeat(2, 1fr);
            gap: 0.75rem;
            height: min(52vw, 620px);
          }
          .hgp-tile--1 {
            grid-column: 1 / span 2;
            grid-row: 1 / span 2;
          }
          .hgp-tile--2 {
            grid-column: 3;
            grid-row: 1;
          }
          .hgp-tile--3 {
            grid-column: 4;
            grid-row: 1;
          }
          .hgp-tile--4 {
            grid-column: 3 / span 2;
            grid-row: 2;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeGalleryPreview',
  displayName: 'Home Gallery Preview',
  tokens: {},
  collectionBindings: {
    'homegallerypreview.photos': {
      label: 'Preview Photos',
      collection: 'photo_gallery',
      description:
        'Photos for the Home page gallery teaser. Items with description FEATURED sort first; the first four eligible photos fill the bento layout.',
    },
  },
};

(HomeGalleryPreview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HomeGalleryPreview;
